/**
 * État de RÉCEPTION d'un compteur Enedis dans la liste des sources — l'icône wifi
 * (DEV-1581, décision A11 du 03/09 ; DEV-1571 pour la mesure du défaut ; DEV-1632 pour
 * la chaîne cliente).
 *
 * ## Le défaut fermé
 *
 * L'icône était VERTE dès que `monitoring.lastdata.date` EXISTAIT : une présence, pas un
 * âge. Mesuré le 02-03/09/2026 : **154 546 compteurs pdl au vert sur une donnée de plus
 * de 30 jours, dont 142 018 de plus d'un an** — à côté d'un texte qui imprimait pourtant
 * la vraie date (« Dernier message reçu le 19 mai 2024 »). Le mensonge était la COULEUR :
 * un signal d'état sans jugement.
 *
 * ## La règle (A11)
 *
 * Toute surface qui colore ou qualifie l'état d'un compteur Enedis consomme le VERDICT
 * unique du service enedis, jamais la présence d'un champ ni un seuil plat du front. La
 * liste le lit via dapi `informations.pdl.data_retrieval.update.state`
 * (`data_retrieval_enedis.py#_etat_update` : `uptodate` ⇔ `verdict.fresh`, `running` ⇔
 * commande en vol, `crashed` ⇔ panne de notre chaîne, `noconsent`, sinon `waiting`).
 * C'est le MÊME bloc que celui d'où descend la bannière : une seule source de vérité
 * pour la couleur et pour la phrase. Une surface qui ne peut pas (encore) le consommer —
 * état `null`, service muet, page pas encore enrichie — n'affiche AUCUN signal : icône
 * neutre, la date nue reste.
 *
 * ⚠️ Le remède « trois paliers d'âge » proposé un temps (DEV-1571) était un seuil plat
 * de plus, interdit par la règle ③ d'`attendus-fraicheur.md` : l'attendu dépend du
 * compteur (segment × régime × mécanisme) et seul enedis le connaît.
 */

/** Configuration visuelle d'un état de réception : icône, couleur, libellé d'infobulle. */
export type ReceptionWifiConfig = {
    icon: string
    color: string
    /** Clé i18n de l'infobulle (résolue par la page). */
    tooltipKey: string
}

/** Vert : à jour au sens du verdict (chaque flux dû dans son délai quand A11 est armé). */
const A_JOUR: ReceptionWifiConfig = {
    icon: "fal fa-wifi",
    color: "var(--p-green-500)",
    tooltipKey: "receptionAJour",
}

/** Orange : quelque chose manque ou court — en attente, retard, commande en vol. */
const EN_ATTENTE: ReceptionWifiConfig = {
    icon: "fal fa-wifi-fair",
    color: "var(--p-orange-500)",
    tooltipKey: "receptionEnAttente",
}

/** Orange aussi, mais une commande court vraiment (le retour du clic « mettre à jour »). */
const EN_COURS: ReceptionWifiConfig = {
    icon: "fal fa-wifi-fair",
    color: "var(--p-orange-500)",
    tooltipKey: "receptionEnCours",
}

/** Rouge : la collecte est BLOQUÉE — panne de notre chaîne, ou consentement absent. */
const BLOQUE: ReceptionWifiConfig = {
    icon: "fal fa-wifi-slash",
    color: "var(--p-red-500)",
    tooltipKey: "receptionBloquee",
}

/** Neutre : on ne SAIT pas — aucun signal d'état, seulement la date nue. */
const INCONNU: ReceptionWifiConfig = {
    icon: "fal fa-wifi",
    color: "var(--p-gray-400)",
    tooltipKey: "receptionInconnue",
}

/**
 * Icône de réception d'un compteur Enedis depuis l'état servi par dapi.
 *
 * @param state - `informations.pdl.data_retrieval.update.state` (ou `null`/`undefined`)
 * @returns La configuration visuelle — jamais un vert par défaut
 */
export function receptionWifiConfig(
    state: string | null | undefined
): ReceptionWifiConfig {
    switch (state) {
        case "uptodate":
            return A_JOUR
        case "running":
            return EN_COURS
        case "waiting":
            return EN_ATTENTE
        case "crashed":
        case "noconsent":
            return BLOQUE
        default:
            // `null`, `unknown`, valeur neuve côté dapi : on ne devine pas un état.
            return INCONNU
    }
}
